/**
 * Stage warnings — the client half of #988's enforcement model.
 *
 * The backend distinguishes two things that used to look identical to the UI:
 *
 *   400 + `{code: 'caretaker_required', message}`  — a Blocked rule. The move
 *       cannot happen. Show the reason; do not offer to continue.
 *   409 + `{code: 'stage_warnings', violations, acknowledge_with}` — expected
 *       information is missing. The move CAN happen; the user reads the list
 *       and either fixes it or continues, and continuing is recorded.
 *
 * Decoding lived inline and copy-pasted in two pages before this, which is why
 * the other four transition call sites showed "Request failed with status code
 * 400" instead of the reason (#977).
 */

export interface StageViolation {
  /** `"<stage>:<field>"` — stable, and what gets acknowledged. NOT `field`:
   *  the cumulative set spans stages, so a field name alone is not unique. */
  code: string
  field: string
  label: string
  message: string
  /** Which stage expected it — the demand may never have sat in that stage. */
  stage: string
  /** True when this move jumps clean over the stage that expected it. */
  skipped: boolean
}

export interface StageWarnings {
  message: string
  violations: StageViolation[]
  sideEffects: string[]
  /** Send back verbatim to confirm. Partial lists are refused. */
  acknowledgeWith: string[]
}

function detailOf(err: unknown): unknown {
  return (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
}

function codeOf(err: unknown): string | null {
  const detail = detailOf(err)
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const code = (detail as { code?: unknown }).code
    if (typeof code === 'string') return code
  }
  return null
}

/**
 * A 409 that the user can acknowledge and proceed through, or null otherwise.
 *
 * Two shapes share this path because they share the whole confirm-and-continue
 * flow:
 *   - `stage_warnings` — a list of unmet expectations (violations).
 *   - `return_to_new` — a plain confirmation with no violations (#1142):
 *     returning a demand to the intake queue is unusual but valid, and the
 *     dialog renders it as a confirmation rather than a fields checklist.
 */
export function parseStageWarnings(err: unknown): StageWarnings | null {
  const code = codeOf(err)
  if (code === 'return_to_new') {
    const d = detailOf(err) as { message?: unknown; acknowledge_with?: unknown }
    return {
      message:
        typeof d.message === 'string'
          ? d.message
          : 'This returns the demand to New — the intake queue.',
      violations: [],
      sideEffects: [],
      acknowledgeWith: Array.isArray(d.acknowledge_with)
        ? (d.acknowledge_with as string[])
        : ['return_to_new'],
    }
  }
  if (code !== 'stage_warnings') return null
  const detail = detailOf(err) as {
    message?: unknown
    violations?: unknown
    side_effects?: unknown
    acknowledge_with?: unknown
  }
  if (!Array.isArray(detail.violations) || !detail.violations.length) return null
  return {
    message:
      typeof detail.message === 'string'
        ? detail.message
        : 'This demand is missing information expected before this point.',
    violations: detail.violations as StageViolation[],
    sideEffects: Array.isArray(detail.side_effects) ? (detail.side_effects as string[]) : [],
    acknowledgeWith: Array.isArray(detail.acknowledge_with)
      ? (detail.acknowledge_with as string[])
      : (detail.violations as StageViolation[]).map((v) => v.code),
  }
}

/** B4: the demand needs an owner before it can leave New. Blocked, not a warning. */
export function isCaretakerRequired(err: unknown): boolean {
  return codeOf(err) === 'caretaker_required'
}

/** Group violations by the stage that expected them, in spine order. */
export function groupByStage(
  violations: StageViolation[],
): Array<{ stage: string; skipped: boolean; items: StageViolation[] }> {
  const order = ['new', 'scoping', 'triaging', 'resourcing']
  const buckets = new Map<string, StageViolation[]>()
  for (const v of violations) {
    const list = buckets.get(v.stage) ?? []
    list.push(v)
    buckets.set(v.stage, list)
  }
  return [...buckets.entries()]
    .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
    .map(([stage, items]) => ({ stage, skipped: items.some((i) => i.skipped), items }))
}
